import React, { useState } from 'react';
import { ShieldAlert, Check, X, AlertTriangle } from 'lucide-react';
import { API_BASE as API } from '../../lib/apiBase';

/* ═══════════════════════════════════════════════════════════════════════════════
   AGE VERIFICATION MODAL (+18)
   
   Required before accessing Pulse Live for the first time.
   - Legal disclaimer
   - User confirms they are 18+
   - Stored in user profile (only asked once)
═══════════════════════════════════════════════════════════════════════════════ */


export const AgeVerificationModal = ({ 
  onVerified, 
  onCancel,
  token,
  lang = 'es' 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleVerify = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/api/live/ticketed-show/verify-age`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Error al verificar edad');
      }

      onVerified?.();

    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
  };

  return (
    <div 
      data-testid="age-verification-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.95)' }}
    >
      <div className="relative w-full max-w-md bg-pulse-surface border border-pulse-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 text-center border-b border-pulse-border">
          <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {lang === 'es' ? 'Verificación de Edad' : 'Age Verification'}
          </h2>
          <p className="text-sm text-pulse-muted">
            {lang === 'es' 
              ? 'Pulse Live es un espacio para mayores de 18 años'
              : 'Pulse Live is a space for adults 18 and over'}
          </p>
        </div>

        {/* Legal Text */}
        <div className="p-6">
          <div className="bg-pulse-elevated border border-pulse-border rounded-xl p-4 mb-6 max-h-48 overflow-y-auto">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <AlertTriangle size={14} className="text-yellow-500" />
              {lang === 'es' ? 'Aviso Legal' : 'Legal Notice'}
            </h3>
            {lang === 'es' ? (
              <div className="text-xs text-pulse-muted space-y-2">
                <p>
                  <strong>Pulse Live</strong> es un entorno de transmisión en vivo destinado exclusivamente a personas mayores de 18 años.
                </p>
                <p>
                  El contenido disponible en esta sección puede incluir:
                </p>
                <ul className="list-disc list-inside pl-2 space-y-1">
                  <li>Lenguaje adulto sin censura</li>
                  <li>Temas y conversaciones para público maduro</li>
                  <li>Contenido artístico sin restricciones de expresión</li>
                </ul>
                <p>
                  Al confirmar que eres mayor de 18 años, aceptas que:
                </p>
                <ul className="list-disc list-inside pl-2 space-y-1">
                  <li>Eres legalmente adulto en tu jurisdicción</li>
                  <li>Comprendes la naturaleza del contenido disponible</li>
                  <li>Accedes voluntariamente a este espacio</li>
                  <li>No compartirás tu acceso con menores de edad</li>
                </ul>
                <p className="pt-2 border-t border-pulse-border mt-2">
                  PulseMusic no se hace responsable del contenido generado por usuarios durante las transmisiones en vivo.
                </p>
              </div>
            ) : (
              <div className="text-xs text-pulse-muted space-y-2">
                <p>
                  <strong>Pulse Live</strong> is a live streaming environment exclusively for individuals 18 years of age or older.
                </p>
                <p>
                  Content available in this section may include:
                </p>
                <ul className="list-disc list-inside pl-2 space-y-1">
                  <li>Uncensored adult language</li>
                  <li>Topics and conversations for mature audiences</li>
                  <li>Artistic content without expression restrictions</li>
                </ul>
                <p>
                  By confirming you are 18+, you accept that:
                </p>
                <ul className="list-disc list-inside pl-2 space-y-1">
                  <li>You are legally an adult in your jurisdiction</li>
                  <li>You understand the nature of available content</li>
                  <li>You voluntarily access this space</li>
                  <li>You will not share your access with minors</li>
                </ul>
                <p className="pt-2 border-t border-pulse-border mt-2">
                  PulseMusic is not responsible for user-generated content during live streams.
                </p>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-sm text-red-400">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              data-testid="age-cancel-btn"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-3 rounded-xl bg-pulse-elevated border border-pulse-border text-pulse-muted font-medium transition hover:text-white hover:border-white/20"
            >
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              data-testid="age-verify-btn"
              onClick={handleVerify}
              disabled={loading}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold transition hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Check size={18} />
                  {lang === 'es' ? 'Soy mayor de 18' : 'I am 18+'}
                </>
              )}
            </button>
          </div>

          {/* Footer Note */}
          <p className="mt-4 text-center text-[10px] text-pulse-muted">
            {lang === 'es' 
              ? 'Esta verificación se guarda en tu perfil y no volverá a solicitarse.'
              : 'This verification is saved to your profile and will not be requested again.'}
          </p>
        </div>
      </div>
    </div>
  );
};
